import { CircleAlert, RefreshCcw } from "lucide-react";
import { DataTable } from "../components/DataTable";
import { Badge, Button, Card, EmptyState, Skeleton } from "../components/ui";
import { useInvoices } from "../data/platform-data";
import { useAssetReceivables } from "../data/assets-platform-data";
import { useTenantRuntime } from "../config/TenantRuntime";

export function FinancePage() {
  const { hasModule } = useTenantRuntime();
  const assetVertical = hasModule("assets");
  const invoiceQuery = useInvoices();
  const receivableQuery = useAssetReceivables();

  const pendingProviderNotice = (
    <aside className="notice" aria-label="Status da integração financeira">
      <CircleAlert size={19} aria-hidden="true" />
      <div>
        <strong>Integração Paglog pendente</strong>
        <p>
          Esta é uma visão de homologação. Cobranças, quitações, Pix, boletos e reconciliações
          continuam indisponíveis até a integração ser homologada.
        </p>
      </div>
    </aside>
  );

  if (assetVertical) {
    return (
      <div className="page-stack">
        {pendingProviderNotice}
        <section className="metric-grid metric-grid-four">
          <article className="metric-card">
            <span>Receita contratada</span>
            <strong>R$ 1,24 mi</strong>
            <small>Simulação de homologação</small>
          </article>
          <article className="metric-card">
            <span>Recebido no mês</span>
            <strong>R$ 72,8 mil</strong>
            <small>Simulação de homologação</small>
          </article>
          <article className="metric-card">
            <span>Vencido atualizado</span>
            <strong>R$ 88,1 mil</strong>
            <small>Atualização demonstrativa</small>
          </article>
          <article className="metric-card">
            <span>Segundas vias</span>
            <strong>26</strong>
            <small>Dados artificiais</small>
          </article>
        </section>

        <Card
          title="Carteira financeira"
          description="Recebíveis de vendas parceladas, imóveis e veículos locados."
          action={
            <Button variant="secondary" disabled title="Disponível após a homologação da Paglog">
              <RefreshCcw size={16} /> Reconciliar Paglog
            </Button>
          }
        >
          {receivableQuery.isLoading && <Skeleton className="table-skeleton" />}
          {receivableQuery.error && (
            <EmptyState
              title="Falha ao carregar financeiro"
              description={receivableQuery.error.message}
            />
          )}
          {!receivableQuery.isLoading && !receivableQuery.error && (
            <DataTable
              rows={receivableQuery.data ?? []}
              getKey={(row) => row.id}
              columns={[
                { key: "agreement", header: "Contrato", render: (row) => row.agreement },
                { key: "customer", header: "Cliente", render: (row) => row.customer },
                { key: "asset", header: "Ativo", render: (row) => row.asset },
                { key: "sequence", header: "Parcela", render: (row) => row.sequence },
                { key: "due", header: "Vencimento", render: (row) => row.dueDate },
                { key: "original", header: "Original", render: (row) => row.original },
                { key: "updated", header: "Atualizado", render: (row) => row.updated },
                {
                  key: "status",
                  header: "Status",
                  render: (row) => (
                    <Badge tone={row.status === "OVERDUE" ? "danger" : "warning"}>
                      {row.status}
                    </Badge>
                  ),
                },
              ]}
            />
          )}
        </Card>
      </div>
    );
  }

  return (
    <div className="page-stack">
      {pendingProviderNotice}
      <Card
        title="Faturas e cobranças"
        description="Visão de homologação preparada para a futura conciliação financeira."
        action={
          <Button variant="secondary" disabled title="Disponível após a homologação da Paglog">
            <RefreshCcw size={16} /> Reconciliar
          </Button>
        }
      >
        {invoiceQuery.isLoading && <Skeleton className="table-skeleton" />}
        {invoiceQuery.error && (
          <EmptyState
            title="Falha ao carregar financeiro"
            description={invoiceQuery.error.message}
          />
        )}
        {!invoiceQuery.isLoading && !invoiceQuery.error && (
          <DataTable
            rows={invoiceQuery.data ?? []}
            getKey={(invoice) => invoice.number}
            columns={[
              {
                key: "number",
                header: "Fatura",
                render: (invoice) => invoice.number,
              },
              {
                key: "customer",
                header: "Cliente",
                render: (invoice) => invoice.customer,
              },
              {
                key: "products",
                header: "Produtos",
                render: (invoice) => invoice.products,
              },
              {
                key: "dueDate",
                header: "Vencimento",
                render: (invoice) => invoice.dueDate,
              },
              {
                key: "total",
                header: "Total",
                render: (invoice) => invoice.total,
              },
              {
                key: "method",
                header: "Meio",
                render: (invoice) => invoice.method,
              },
              {
                key: "status",
                header: "Status",
                render: (invoice) => (
                  <Badge
                    tone={
                      invoice.status === "PAID" || invoice.status === "PAGA"
                        ? "success"
                        : invoice.status === "OVERDUE" || invoice.status === "VENCIDA"
                          ? "danger"
                          : "warning"
                    }
                  >
                    {invoice.status}
                  </Badge>
                ),
              },
            ]}
          />
        )}
      </Card>
    </div>
  );
}
